import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PostCard from "../components/PostCard";
import { toast } from "sonner";
import { ArrowLeft, Search, Loader2, Sparkles, TrendingUp, Bot, Users, Home, Plus, Bell, User, Globe, ExternalLink, AlertTriangle } from "lucide-react";
import api from "../lib/api";

const PROVIDER_BADGES = {
  brave: { label: "Brave", className: "bg-orange-500/15 text-orange-300" },
  duckduckgo: { label: "DuckDuckGo", className: "bg-amber-500/15 text-amber-300" },
  ai_fallback: { label: "AI-generated", className: "bg-purple-500/15 text-purple-300" },
};

function faviconFor(url) {
  try {
    return `https://www.google.com/s2/favicons?sz=32&domain=${new URL(url).hostname}`;
  } catch {
    return null;
  }
}

export default function ExplorePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [profile, setProfile] = useState(null);

  // AI search — initialize from URL ?mode=web so deep links work.
  const initialMode = searchParams.get("mode") === "web" ? "web" : "posts";
  const [searchMode, setSearchMode] = useState(initialMode);
  const [aiResult, setAiResult] = useState(null);
  const [aiSearching, setAiSearching] = useState(false);
  const [aiError, setAiError] = useState("");

  useEffect(() => {
    const mode = searchParams.get("mode");
    if (mode === "web" || mode === "posts") setSearchMode(mode);
  }, [searchParams]);

  const sentinelRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    api.get("/profile/me").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  const loadPosts = useCallback(async (p = 1, append = false) => {
    if (p === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 10, post_type: filter });
      if (searchQuery && searchMode === "posts") params.set("search", searchQuery);
      const r = await api.get(`/posts/explore?${params}`);
      if (append) setPosts((prev) => [...prev, ...r.data.posts]);
      else setPosts(r.data.posts);
      setHasMore(r.data.has_more);
      setPage(p);
    } catch { toast.error("Failed to load posts"); }
    finally { setLoading(false); setLoadingMore(false); }
  }, [filter, searchQuery, searchMode]);

  useEffect(() => { loadPosts(1); }, [loadPosts]);

  // Infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    observerRef.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore && !loadingMore && !loading) loadPosts(page + 1, true);
    }, { threshold: 0.1 });
    observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [hasMore, loadingMore, loading, page, loadPosts]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = searchInput.trim();
    if (!q) return;
    if (searchMode === "web") {
      setAiSearching(true);
      setAiResult(null);
      setAiError("");
      try {
        const r = await api.post("/search", { query: q, type: "web" });
        setAiResult(r.data);
      } catch (err) {
        const msg = err.response?.status === 429
          ? "You've hit your daily AI search limit. Try again tomorrow."
          : "Search failed. Please try again.";
        setAiError(msg);
        toast.error("Search failed");
      }
      finally { setAiSearching(false); }
    } else {
      setSearchQuery(q);
      setAiResult(null);
      setAiError("");
    }
  };

  return (
    <div className="min-h-screen pb-20 lg:pb-0" data-testid="explore-page">
      {/* Top bar */}
      <div className="sticky top-0 z-30 nav-glass px-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3 h-12">
          <button onClick={() => navigate("/feed")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer"><ArrowLeft className="w-5 h-5" /></button>
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
            <input
              data-testid="explore-search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={searchMode === "web" ? "Ask anything..." : "Search posts..."}
              className="w-full pl-9 pr-4 py-2 bg-muted/40 border border-border rounded-full text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400"
            />
          </form>
        </div>
        {/* Search Mode + Filter tabs */}
        <div className="max-w-2xl mx-auto flex items-center gap-3 pb-2">
          {/* Mode toggle */}
          <div className="flex bg-muted/70 rounded-full p-0.5 mr-2" data-testid="search-mode-toggle">
            <button onClick={() => { setSearchMode("posts"); setAiResult(null); setAiError(""); }} className={`px-3 py-1 rounded-full text-xs font-medium border-0 cursor-pointer transition-all ${searchMode === "posts" ? "bg-card text-cyan-400 shadow-sm" : "bg-transparent text-muted-foreground"}`} data-testid="mode-posts">
              <Search className="w-3 h-3 inline mr-1" />Posts
            </button>
            <button onClick={() => setSearchMode("web")} className={`px-3 py-1 rounded-full text-xs font-medium border-0 cursor-pointer transition-all ${searchMode === "web" ? "bg-card text-cyan-400 shadow-sm" : "bg-transparent text-muted-foreground"}`} data-testid="mode-web">
              <Globe className="w-3 h-3 inline mr-1" />Web (AI)
            </button>
          </div>
          {searchMode === "posts" && [
            { id: "all", label: "All", icon: TrendingUp },
            { id: "ai_generated", label: "AI Generated", icon: Bot },
            { id: "user", label: "User Posts", icon: Users },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border-0 cursor-pointer transition-all ${
                filter === f.id ? "bg-cyan-500/10 text-cyan-400" : "bg-transparent text-muted-foreground hover:bg-white/5"
              }`}
              data-testid={`filter-${f.id}`}
            >
              <f.icon className="w-3.5 h-3.5" /> {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* AI Search Results */}
        {searchMode === "web" && (aiSearching || aiResult || aiError) && (
          <div className="mb-6">
            {aiSearching ? (
              <div className="bg-card rounded-xl border border-cyan-500/20 p-5" data-testid="web-search-skeleton">
                <div className="flex items-center gap-2 mb-4">
                  <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
                  <p className="text-sm text-muted-foreground">Searching the web with AI...</p>
                </div>
                <div className="h-4 w-full skeleton mb-2" />
                <div className="h-4 w-5/6 skeleton mb-2" />
                <div className="h-4 w-2/3 skeleton mb-4" />
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded skeleton flex-shrink-0" />
                      <div className="h-3 w-1/2 skeleton" />
                    </div>
                  ))}
                </div>
              </div>
            ) : aiError ? (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-5 text-center" data-testid="web-search-error">
                <AlertTriangle className="w-5 h-5 text-red-400 mx-auto mb-2" />
                <p className="text-sm text-red-400 mb-3">{aiError}</p>
                <button onClick={handleSearch} className="text-xs font-medium text-cyan-400 bg-card border border-cyan-500/30 rounded-full px-4 py-1.5 cursor-pointer hover:bg-cyan-500/10" data-testid="web-search-retry">
                  Try again
                </button>
              </div>
            ) : aiResult ? (() => {
              const badge = PROVIDER_BADGES[aiResult.provider] || null;
              const aiGenerated = !!aiResult.ai_generated_sources;
              // Prefer retrieved results (they carry snippets); fall back to
              // the bare sources list on the LLM-only path.
              const sources = aiResult.results?.length ? aiResult.results : (aiResult.sources || []);
              return (
                <div className={`rounded-xl p-5 border ${aiGenerated ? "bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30" : "bg-gradient-to-br from-cyan-500/10 to-violet-500/10 border-cyan-500/20"}`} data-testid="ai-search-result">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <Globe className={`w-4 h-4 ${aiGenerated ? "text-amber-500" : "text-cyan-400"}`} />
                    <span className={`text-xs font-medium ${aiGenerated ? "text-amber-300" : "text-cyan-400"}`}>AI Web Search</span>
                    {badge && (
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${badge.className}`} data-testid="search-provider-badge">
                        {badge.label}
                      </span>
                    )}
                  </div>
                  {aiGenerated && aiResult.disclaimer && (
                    <div className="flex items-start gap-2 bg-amber-500/15 border border-amber-500/30 rounded-lg px-3 py-2 mb-3" data-testid="ai-sources-disclaimer">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-300">{aiResult.disclaimer}</p>
                    </div>
                  )}
                  {aiResult.ai_summary && (
                    <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap mb-3" data-testid="ai-search-answer">{aiResult.ai_summary}</p>
                  )}
                  {sources.length > 0 && (
                    <div className={`border-t pt-3 ${aiGenerated ? "border-amber-500/30" : "border-cyan-500/20"}`}>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-2">
                        Sources{aiGenerated ? " (AI-suggested, unverified)" : ""}
                      </p>
                      <div className="space-y-2.5" data-testid="search-sources">
                        {sources.map((s, i) => {
                          const favicon = s.url ? faviconFor(s.url) : null;
                          return (
                            <div key={i} className="flex items-start gap-2 min-w-0" data-testid={`search-source-${i}`}>
                              {favicon ? (
                                <img src={favicon} alt="" className="w-4 h-4 rounded mt-0.5 flex-shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                              ) : (
                                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground/70 mt-0.5 flex-shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                {s.url ? (
                                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="block text-xs font-medium text-cyan-400 hover:text-cyan-300 no-underline truncate">{s.title || s.url}</a>
                                ) : (
                                  <span className="block text-xs font-medium text-foreground/80 truncate">{s.title}</span>
                                )}
                                {s.url && <p className="text-[10px] text-muted-foreground/70 truncate">{s.url}</p>}
                                {s.snippet && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{s.snippet}</p>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })() : null}
          </div>
        )}

        {/* Posts */}
        {searchMode === "posts" && (
          <>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-card rounded-xl border border-border/60 p-4">
                    <div className="flex gap-3 mb-3"><div className="w-10 h-10 rounded-full skeleton" /><div className="flex-1"><div className="h-4 w-32 skeleton mb-1" /><div className="h-3 w-20 skeleton" /></div></div>
                    <div className="h-4 w-full skeleton mb-2" /><div className="h-4 w-2/3 skeleton" />
                  </div>
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-16 animate-fade-in-up" data-testid="explore-empty">
                <Sparkles className="w-8 h-8 text-muted-foreground/60 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">{searchQuery ? "No posts match your search" : "No posts to explore yet"}</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="explore-posts">
                {posts.map((post) => <PostCard key={post.id} post={post} />)}
                <div ref={sentinelRef} className="h-4" />
                {loadingMore && <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-cyan-400 animate-spin" /></div>}
                {!hasMore && posts.length > 5 && <p className="text-center text-xs text-muted-foreground/70 py-4">You've seen everything!</p>}
              </div>
            )}
          </>
        )}
      </div>

      {/* Mobile Bottom Tabs */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border/60 h-14 flex items-center justify-around px-2">
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Home className="w-5 h-5" /><span className="text-[10px]">Home</span></Link>
        <Link to="/explore" className="flex flex-col items-center gap-0.5 no-underline text-cyan-400"><Search className="w-5 h-5" /><span className="text-[10px]">Explore</span></Link>
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Plus className="w-5 h-5" /></Link>
        <Link to="/notifications" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Bell className="w-5 h-5" /><span className="text-[10px]">Notifs</span></Link>
        <Link to={profile ? `/profile/${profile.username}` : "/feed"} className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><User className="w-5 h-5" /><span className="text-[10px]">Profile</span></Link>
      </div>
    </div>
  );
}
